import * as SentryCapacitor from '@sentry/capacitor'
import { bs } from './state/bound.store'
import { captureSentryMessage, isWebPreview } from './utils'
import { reactAppStage } from './utils/reactAppStage'

let breadcrumbCounter = 0

const sentryEnabled = () => {
    if (isWebPreview) return false
    if (import.meta.env.MODE !== 'production') return false
    if (reactAppStage() === 'demo') return true
    if (reactAppStage() === 'test') return true
    return false
}

export function initSentry() {
    console.log('initSentry')

    SentryCapacitor.init({
        dsn: 'https://b5ce1302050540cd953cdbf657ce4817@sentry.softpauer.com/2',
        maxBreadcrumbs: 200,
        beforeBreadcrumb(breadcrumb, hint) {
            if (breadcrumbCounter > 200) {
                breadcrumbCounter = 0
                captureSentryMessage(
                    'Logs for ' + (bs().profile?.email ?? 'NO_USER')
                )
            }
            breadcrumbCounter += 1
            if ((breadcrumb.message?.length ?? 0) > 10000) {
                breadcrumb.message =
                    breadcrumb?.message?.substring(0, 10000) + '...'
            }
            return breadcrumb
        },
        release: import.meta.env.VITE_APP_VERSION,
        enabled: sentryEnabled(),
        environment: reactAppStage(),

        // Tracing
        tracesSampleRate: 1.0, // Capture 100% of the transactions
        tracePropagationTargets: [
            'localhost',
            /^https:\/\/yourserver\.io\/api/,
        ],
        integrations: [
            SentryCapacitor.browserTracingIntegration(),
            SentryCapacitor.replayIntegration({
                networkDetailAllowUrls: [
                    'https://wp.t-i.eventr.softpauer.com',
                    'https://wp.d-i.eventr.softpauer.com',
                    'https://wp.l.eventr.softpauer.com',
                    'https://api.t-i.eventr.softpauer.com',
                    'https://api.d-i.eventr.softpauer.com',
                    'https://api.l.eventr.softpauer.com',
                    'https://account.t-i.eventr.softpauer.com',
                    'https://account.d-i.eventr.softpauer.com',
                    'https://account.l.eventr.softpauer.com',
                ],
                networkRequestHeaders: ['X-SP-Trace-Id'],
                blockAllMedia: false,
                maskAllText: false,
            }),
        ],
        beforeSend: function (event, hint) {
            // filter out UnhandledRejection errors that have no information
            if (
                event !== undefined &&
                event.exception !== undefined &&
                event.exception.values !== undefined &&
                event.exception.values.length === 1
            ) {
                var e = event.exception.values[0]
                if (
                    e.type === 'UnhandledRejection' &&
                    e.value?.startsWith(
                        'Object captured as promise rejection with keys'
                    )
                ) {
                    return null
                }
            }
            // filter out AxiosError errors that have no information
            if (event.exception?.values?.[0].type === 'AxiosError') {
                return null
            }
            if (event.exception?.values?.[0].type === 'InvalidStateError') {
                if (
                    event.exception?.values?.[0].value ===
                    'Skipping view transition because viewport size changed.'
                ) {
                    return null
                }
                if (
                    event.exception?.values?.[0].value ===
                    "Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing."
                ) {
                    return null
                }
            }
            if (event.exception?.values?.[0].type === 'Error') {
                if (
                    event.exception?.values?.[0].value ===
                    'InvalidStateError: Object store cannot be found in the database'
                ) {
                    return null
                }
                if (
                    event.exception?.values?.[0].value ===
                    'UnknownError: Attempt to delete range from database without an in-progress transaction'
                ) {
                    return null
                }
                if (
                    event.exception?.values?.[0].value ===
                    'UnknownError: Connection to Indexed Database server lost. Refresh the page to try again'
                ) {
                    return null
                }
                if (
                    event.exception?.values?.[0].value ===
                    'UnknownError: An internal error was encountered in the Indexed Database server'
                ) {
                    return null
                }
                if (
                    event.exception?.values?.[0].value ===
                    'UnknownError: Attempt to get a record from database without an in-progress transaction'
                ) {
                    return null
                }
            }
            if (event.exception?.values?.[0].type === 'AbortError') {
                if (
                    event.exception?.values?.[0].value ===
                    'Old view transition aborted by new view transition.'
                ) {
                    return null
                }
            }
            if (event.exception?.values?.[0].type === 'CanceledError') {
                if (
                    event.exception?.values?.[0].value ===
                    'Request cancelled: No connectivity.'
                ) {
                    return null
                }
            }

            return event
        },
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,
    })
}
